import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import StudentPerformanceReportPdfGenerator from "../../components/PdfGenerator/StudentPerformanceReportPdfGenerator";
import StudentResultCardReportPdfGenerator from "../../components/PdfGenerator/StudentResultCardReportPdfGenerator";

function StudentResultCardReport() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [criteriaId, setCriteriaId] = useState(null);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);

  const [termsData, setTermsData] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [studentsData, setStudentsData] = useState([]);

  const [reportData, setReportData] = useState(null);

  const fetchTermsByOrgId = async () => {
    try {
      const response = await get(
        `StudentEvaluation/GetAllEvaluationCriteriaType?OrganizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTermsData(response?.evaluationTypes);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  useEffect(() => {
    fetchTermsByOrgId();
  }, []);

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );

          if (selectedCenter) {
            // Set the selected center in the form
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });

            fetchClassSectionsByCenterId(savedCenterId);

            // Fetch calendars for the saved center
            // fetchCalendars(selectedCenter.id, selectedYear);
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    fetchCenters();
  }, []);

  const fetchClassSectionsByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `ClassSection/GetAllClassSectionByCenterId?centerId=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setClassSectionsData(response);
      } else {
        setClassSectionsData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchStudentsByClassSectionId = async () => {
    try {
      const response = await get(
        `Student/GetStudentByClassSectionIdAsync?classSectionId=${selectedClassSectionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setStudentsData(response || []);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  useEffect(() => {
    if (selectedClassSectionId) {
      fetchStudentsByClassSectionId();
    } else {
      setStudentsData([]);
    }
  }, [selectedClassSectionId]);

  const handleTermChange = (selectedOption, actionMeta) => {
    if (actionMeta.action === "clear") {
      setCriteriaId(null);
      setSelectedClassSectionId(null);
      setSelectedStudentId(null);

      setClassSectionsData([]);
      setStudentsData([]);

      setValue("criteriaId", null);
      setValue("classSectionId", null);
      setValue("studentId", null);
    } else {
      setCriteriaId(selectedOption ? selectedOption.value : null);
      setValue("criteriaId", selectedOption);
    }
  };

  const saveData = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Student/GetStudentResultCard?centerId=${centerId}&criteriaId=${criteriaId}&classSectionId=${selectedClassSectionId}&studentId=${selectedStudentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.length) {
        setReportData(response);
      } else {
        toast.error("No data found for this report");
        setReportData(null);
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Student Result Card Report</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit(saveData)}>
            <div className="row g-3">
              <div className="col-md-2">
                <label className="form-label">
                  Center<span className="text-danger fs-9">*</span>
                </label>
                <Controller
                  name="centerId"
                  control={control}
                  rules={{ required: "This field is required." }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={centersData.map((center) => ({
                        value: center.id,
                        label: center.name,
                      }))}
                      isDisabled={savedCenterId !== "0"}
                      onChange={(selectedOption, actionMeta) => {
                        if (actionMeta.action === "clear") {
                          setValue("centerId", null);
                        } else {
                          setValue("centerId", selectedOption);
                          setCenterId(selectedOption.value);
                          fetchClassSectionsByCenterId(selectedOption.value);
                        }
                        setClassSectionsData([]);
                        setValue("classSectionId", null);
                        setSelectedClassSectionId(null);
                        setStudentsData([]);
                        setValue("studentId", null);
                        setSelectedStudentId(null);

                        trigger("centerId");
                      }}
                      className="react-select-style"
                      classNamePrefix="react-select-style-pref"
                      // isClearable
                    />
                  )}
                />
                {errors.centerId && (
                  <small className="text-danger">
                    {errors.centerId.message}
                  </small>
                )}
              </div>
              <div className="col-md-2">
                <label className="form-label">
                  Term<span className="text-danger fs-9">*</span>
                </label>
                <Controller
                  name={`criteriaId`}
                  control={control}
                  rules={{ required: "This field is required." }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={termsData.map((term) => ({
                        value: term.id,
                        label: term.type,
                      }))}
                      onChange={handleTermChange}
                      className="react-select-style"
                      classNamePrefix="react-select-style-pref"
                      // isClearable
                    />
                  )}
                />
                {errors.criteriaId && (
                  <small className="text-danger">
                    {errors.criteriaId.message}
                  </small>
                )}
              </div>
              <div className="col-md-2">
                <label className="form-label">
                  Year Group - Section
                  <span className="text-danger fs-9">*</span>
                </label>
                <Controller
                  name="classSectionId"
                  control={control}
                  rules={{ required: "This field is required." }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={classSectionsData.map((classSection) => ({
                        value: classSection.id,
                        label: classSection.name,
                      }))}
                      onChange={(selectedOption, actionMeta) => {
                        if (actionMeta.action === "clear") {
                          setValue("classSectionId", null);
                          setSelectedClassSectionId(null);
                        } else {
                          setValue("classSectionId", selectedOption);
                          setSelectedClassSectionId(selectedOption.value);
                        }
                        setValue("studentId", null);
                        setSelectedStudentId(null);
                        setStudentsData([]);
                        trigger("classSectionId");
                      }}
                      className="react-select-style"
                      classNamePrefix="react-select-style-pref"
                      // isClearable
                    />
                  )}
                />
                {errors.classSectionId && (
                  <small className="text-danger">
                    {errors.classSectionId.message}
                  </small>
                )}
              </div>
              <div className="col-md-2">
                <label className="form-label">
                  Student<span className="text-danger fs-9">*</span>
                </label>
                <Controller
                  name="studentId"
                  control={control}
                  rules={{ required: "This field is required." }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={studentsData.map((student) => ({
                        value: student.id,
                        label: student.studentName + " — " + student.code,
                      }))}
                      onChange={(selectedOption, actionMeta) => {
                        if (actionMeta.action === "clear") {
                          setSelectedStudentId(null);
                          setValue("studentId", null);
                        } else {
                          setSelectedStudentId(
                            selectedOption ? selectedOption.value : null
                          );
                          setValue("studentId", selectedOption);
                        }
                      }}
                      className="react-select-style"
                      classNamePrefix="react-select-style-pref"
                    />
                  )}
                />
                {errors.studentId && (
                  <small className="text-danger">
                    {errors.studentId.message}
                  </small>
                )}
              </div>
              <div className="col">
                <label className="form-label" style={{ visibility: "hidden" }}>
                  Action
                </label>
                <button
                  className="btn btn-primary px-3"
                  type="submit"
                  disabled={isSubmitting}
                  style={{ paddingBlock: "5px" }}
                >
                  {isSubmitting ? "Generating" : "Generate"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {reportData?.length && (
        <div className="card mt-3">
          <div className="card-body">
            <StudentResultCardReportPdfGenerator reportData={reportData} />
          </div>
        </div>
      )}
    </>
  );
}

export default StudentResultCardReport;
